'use client'
import { useResponsiveSize } from "@/provider/useResponsiveSize"
import { usePathname } from "@/locales/routing"
import { useDevice } from "@/provider/DecideProvider"
import { ScrollElementProvider } from "@/provider/ScrollElementProvider"
import { cn } from "@heroui/react"
import { motion } from "framer-motion"
import { memo, PropsWithChildren, useEffect, useMemo, useRef } from "react"
import { Header } from "./Header"
import { useRootLayout } from "./useRootLayout"
import { ScrollEdgeButton } from "@/components/ScrollEdgeButton"

const animateStyle = cn(
  // default/asideClose（侧边栏留白）只在 lg 及以上生效：SSR 只能按 UA 猜视口，
  // 桌面 UA + 窄窗口（未最大化 / 系统缩放 125%）时曾输出 default，挂载后 JS 实测宽度
  // 再切成 asideHidden，配合 transition-[padding] 造成整屏内容左滑"跳一下"。
  // 改为 CSS 断点控制后，<1024px 时留白直接为窄版，不依赖 JS 校正，无跳动。
  "lg:group-data-[animate=default]/layout:pl-[19.125rem] lg:group-data-[animate=default]/layout:pr-[1.625rem] max-lg:group-data-[animate=default]/layout:px-[0.75rem]",
  "group-data-[animate=asideHidden]/layout:px-[0.75rem]",
  "lg:group-data-[animate=asideClose]/layout:pl-[calc(56px_+_7.18vw)] lg:group-data-[animate=asideClose]/layout:pr-[7.18vw] max-lg:group-data-[animate=asideClose]/layout:px-[0.75rem]",
  // "group-data-[animate=chatAsideClose]/layout:pl-[3.5rem] group-data-[animate=chatAsideClose]/layout:pr-0",
  "group-data-[animate=chatAsideHidden]/layout:px-0",
  // "group-data-[animate=chatAsideDefault]/layout:pl-[17.5rem] group-data-[animate=chatAsideDefault]/layout:pr-0",
)

const headerStyle = cn(
  "data-[header=default]:border-b-0 data-[header=default]:translate-y-0 sticky",
  "data-[header=outline]:border-b-1 data-[header=outline]:translate-y-0 sticky",
  "data-[header=hidden]:border-b-0 transition-none data-[header=hidden]:translate-y-[-80px] data-[header=hidden]:fixed",
)

export const isLikeChatLayout = (pathname) => pathname.includes('chats')
export const isLikeGenerateLayout = (pathname) => pathname.includes('generate')
// 全高编辑工作台（Lorebook 编辑、角色导入）：隐藏全局 Header，页面自管滚动
export const isLikeEditorLayout = (pathname) => pathname.includes('/create/lorebook') || pathname.includes('/create/import')


export const Layout = memo(({ children, ssrLogined = false }: PropsWithChildren & { ssrLogined?: boolean }) => {
  const [aside, headerState, asideWrapper] = useRootLayout(state => [state.aside, state.header, state.asideWrapper])
  const container = useRef(null)
  const target = useRef(null)
  const device = useDevice()
  const size = useResponsiveSize(['default', 'sm', 'lg'])


  const pathname = usePathname()

  // 由aside 控制的动画初始
  const initAnimate = useMemo(() => {
    if (device === 'mobile') {
      if (isLikeChatLayout(pathname) || isLikeGenerateLayout(pathname) || isLikeEditorLayout(pathname)) {
        return 'chatAsideHidden'
      }

      return 'asideHidden'
    } else {
      if (isLikeChatLayout(pathname)) {
        return 'chatAsideClose'
      } else if (pathname.includes('search')) {
        return 'asideHidden'
      } else {
        return 'default'
      }
    }

  }, [])

  const animate = useMemo(() => {
    // 未交互侧边栏时：优先按视口宽度判断（size 挂载后取自 window.innerWidth），
    // 避免部分手机 UA 被误判为 PC 时卡在电脑布局。
    if (!aside) {
      if (size !== 'lg') {
        return isLikeChatLayout(pathname) || isLikeGenerateLayout(pathname) || isLikeEditorLayout(pathname)
          ? 'chatAsideHidden'
          : 'asideHidden'
      }
      return initAnimate
    }
    if (size !== 'lg') {
      if (isLikeChatLayout(pathname) || isLikeGenerateLayout(pathname) || isLikeEditorLayout(pathname)) {
        return 'chatAsideHidden'
      }
      return 'asideHidden'
    } else {
      if (aside === "close") {
        return isLikeChatLayout(pathname) ? 'chatAsideClose' : 'asideClose'
      } else if (asideWrapper === 'hidden') {
        return 'asideHidden'
      }
      return isLikeChatLayout(pathname)  ? 'chatAsideHidden' : 'default'
    }

  }, [aside, pathname, size, asideWrapper])



  // 是否需要 溢出隐藏( 当滚动是由页面自己控制时 )：
  // true 任何宽度都由页面自管滚动；'lg' 仅 lg 及以上自管（桌面详情页）。
  // 'lg' 必须交给 CSS 断点判断而不能用 JS 的 size：SSR 只能按 UA 猜 size，
  // 桌面 UA + 窄窗口（<1024，如系统缩放 125%/150%）时挂载后 size 才从 lg 纠正为 default，
  // overflow/stretch 随之切换会让整屏详情内容产生约 0.17 的 layout-shift（CLS）。
  const isOverflowHidden = useMemo((): boolean | 'lg' => {

    if (isLikeChatLayout(pathname)) return true
    // Agent 会话页、生产画布、Lorebook 编辑 / 角色导入页自管滚动（全高工作台布局）
    if (pathname.includes('/generate/agent/') || pathname.includes('/generate/canvas')) return true
    if (isLikeEditorLayout(pathname)) return true
    if (pathname.includes('robot')) return 'lg'
    return false
  }, [pathname])


  const headerAnimate = useMemo(() => {
    return isLikeChatLayout(pathname) || isLikeGenerateLayout(pathname) || isLikeEditorLayout(pathname)
      ? 'hidden'
      : headerState
  }, [pathname, headerState])


  return (<>


    {/* main */}

    <main
      data-overhidden={isOverflowHidden}
      data-device={device}
      data-size={size}
      data-animate={animate}
      suppressHydrationWarning
      className={cn(
        "group/layout box-border transition-all relative w-full h-full overflow-y-auto",
        "overflow-y-hidden",
        "flex flex-col",
      )
      }
    >
      <ScrollElementProvider value={{
        container: container,
        target: target
      }}>
        {/* header */}
        <div
          data-header={headerAnimate}
          className={cn(
            "border-b-theme-primary/10 z-[29] sticky h-16 justify-center w-full flex items-center bg-surface",
            "top-0 left-0 transition-transform",
            "flex-shrink-0",
            headerStyle
          )}
        >
          <div
            className={cn(
              "flex-1 flex justify-center transition-[padding]",
              animateStyle)}
          >
            <Header ssrLogined={ssrLogined} />
          </div>
        </div>

        <div
          data-chats={isLikeChatLayout(pathname)}
          ref={container}

          className={cn(
            // display 必须始终为 flex，不能随 overhidden 切换：
            // 桌面端从可滚动页（如首页）导航到 robot 页时，block→flex 的切换会让
            // Chromium 对这个持久滚动容器误报一次 64px（header 高）的幻影 layout-shift，
            // 画面实际没动，但 CLS 被累计，导致 GSC 报"CLS 超过 0.1（桌面）"。
            "flex justify-center h-auto transition-[padding] overflow-auto",
            // 非 overhidden 页面（移动端详情页/首页等）由本容器滚动：
            // flex 默认 align-items:stretch 会把子元素压成容器高度，内容被裁掉、
            // 整页无法下滑（只剩内部自带 overflow 的小块能滚）。items-start 恢复
            // 子元素自然高度（等价旧 block 布局），内容超出才能撑出滚动。
            // overhidden 页面（聊天页/桌面详情页）页面自己管滚动，保持 stretch 撑满。
            "items-start group-data-[overhidden=true]/layout:items-stretch lg:group-data-[overhidden=lg]/layout:items-stretch",
            "group-data-[overhidden=true]/layout:overflow-y-hidden lg:group-data-[overhidden=lg]/layout:overflow-y-hidden",
            "flex-1",
            animateStyle
          )}
        >
          {children}
        </div>

        {/* 非聊天页(首页/搜索等普通滚动页)：向下滚动后显示“回到顶部”按钮 */}
        {!isLikeChatLayout(pathname) && !isLikeEditorLayout(pathname) && (
          <ScrollEdgeButton mode="top" className="bottom-6 right-6 z-30" />
        )}
      </ScrollElementProvider>
    </main>
  </>

  )
})